/**
 * Suomalainen viitenumero (kotimainen muoto). Tarkiste lasketaan painoilla 7, 3, 1 oikealta alkaen.
 * RF-muoto lisätään myöhemmin.
 */
const WEIGHTS = [7, 3, 1] as const;

function checkDigit(base: string): number {
  let sum = 0;
  for (let i = 0; i < base.length; i++) {
    const digit = Number(base[base.length - 1 - i]);
    sum += digit * WEIGHTS[i % 3]!;
  }
  return (10 - (sum % 10)) % 10;
}

/** Perusosa 3–19 numeroa → viitenumero tarkisteineen. */
export function createReference(base: string | number): string {
  const digits = String(base);
  if (!/^\d{3,19}$/.test(digits)) {
    throw new Error('Viitenumeron perusosan pitää olla 3–19 numeroa');
  }
  return digits + checkDigit(digits);
}

export function isValidReference(reference: string): boolean {
  const digits = reference.replace(/\s/g, '');
  if (!/^\d{4,20}$/.test(digits)) return false;
  return checkDigit(digits.slice(0, -1)) === Number(digits.slice(-1));
}

/** "1234561" → "12 34561" (viiden ryhmät oikealta, kuten laskuissa). */
export function formatReference(reference: string): string {
  const digits = reference.replace(/\s/g, '');
  const groups: string[] = [];
  for (let end = digits.length; end > 0; end -= 5) {
    groups.unshift(digits.slice(Math.max(0, end - 5), end));
  }
  return groups.join(' ');
}

/** "fi21 1234 5600 0007 85" → "FI2112345600000785". */
export function normalizeIban(input: string): string {
  return input.replace(/\s/g, '').toUpperCase();
}

/** IBAN-tarkiste (mod 97). Ottaa vastaan normalisoidun tai välilyönnein kirjoitetun. */
export function isValidIban(input: string): boolean {
  const iban = normalizeIban(input);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  if (iban.startsWith('FI') && iban.length !== 18) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const value = ch >= 'A' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const digit of value) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

/** "FI2112345600000785" → "FI21 1234 5600 0007 85". */
export function formatIban(iban: string): string {
  return normalizeIban(iban).replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * Pankkiviivakoodin numerosarja (versio 4, kotimainen viite). Maksaja voi liittää sen
 * verkkopankkiin, jolloin tilinumero, summa, viite ja eräpäivä täyttyvät itse.
 * Vain suomalaisille tilinumeroille ja alle miljoonan euron summille; muuten null.
 */
export function virtualBarcode(
  iban: string,
  cents: number,
  reference: string,
  dueDate: string,
): string | null {
  const account = normalizeIban(iban);
  const ref = reference.replace(/\s/g, '');
  if (!account.startsWith('FI') || account.length !== 18) return null;
  if (cents < 0 || cents >= 100_000_000 || ref.length > 20) return null;
  const euros = String(Math.floor(cents / 100)).padStart(6, '0');
  const rest = String(cents % 100).padStart(2, '0');
  const [y, m, d] = dueDate.split('-');
  return `4${account.slice(2)}${euros}${rest}000${ref.padStart(20, '0')}${y!.slice(2)}${m}${d}`;
}
